import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export interface MapZoomTarget {
  lat: number;
  lng: number;
  zoom: number;
}

export interface InteractiveMapHandle {
  flyTo: (target: MapZoomTarget, durationSec?: number) => Promise<void>;
  reset: () => void;
}

const InteractiveMap = forwardRef<InteractiveMapHandle>((_props, ref) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useImperativeHandle(ref, () => ({
    flyTo: (target, durationSec = 5) => {
      return new Promise<void>((resolve) => {
        const map = mapInstanceRef.current;
        if (!map) { resolve(); return; }

        // Re-enable zoom for the animation
        map.setMinZoom(1);
        map.setMaxZoom(18);

        map.flyTo([target.lat, target.lng], target.zoom, {
          duration: durationSec,
          easeLinearity: 0.35, // snappy ease-in-out
        });

        map.once("moveend", () => resolve());

        // Safety timeout in case moveend doesn't fire
        setTimeout(resolve, (durationSec + 1) * 1000);
      });
    },
    reset: () => {
      const map = mapInstanceRef.current;
      if (!map) return;
      map.flyTo([20, 0], 2, { duration: 1.5 });
      setTimeout(() => {
        map.setMinZoom(2);
        map.setMaxZoom(2);
      }, 1600);
    },
  }));

  useEffect(() => {
    const el = mapRef.current;
    if (!el || mapInstanceRef.current) return;

    const map = L.map(el, {
      center: [20, 0],
      zoom: 2,
      minZoom: 2,
      maxZoom: 2,
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      keyboard: false,
      boxZoom: false,
      zoomAnimation: true,
      fadeAnimation: true,
    });

    mapInstanceRef.current = map;

    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      detectRetina: true,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  return (
    <div className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
      <div
        ref={mapRef}
        className="w-full h-full"
        style={{ background: "#a8d0e6" }}
      />
      <div className="absolute inset-0 pointer-events-none sky-gradient" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="cloud cloud-1" />
        <div className="cloud cloud-2" />
        <div className="cloud cloud-3" />
      </div>
      <div className="absolute inset-0 pointer-events-none vignette-overlay" />
    </div>
  );
});

InteractiveMap.displayName = "InteractiveMap";

export default InteractiveMap;
